import React, { useEffect, useState } from "react";
import { X, ChevronLeft, ChevronRight } from "lucide-react";
import { API } from "@/lib/api";

export default function PhotoGallery({ photos, token, onClose }) {
  const [idx, setIdx] = useState(0);
  const [src, setSrc] = useState(null);
  const [zoomed, setZoomed] = useState(false);

  useEffect(() => {
    if (!photos.length) return;
    let active = true;
    let url = null;
    const current = photos[idx];
    fetch(`${API}/files/photo/${current.id}?token=${encodeURIComponent(token)}`)
      .then((r) => r.ok ? r.blob() : Promise.reject(new Error("photo fetch failed")))
      .then((blob) => {
        if (!active) return;
        url = URL.createObjectURL(blob);
        setSrc(url);
      })
      .catch(() => active && setSrc(null));
    return () => {
      active = false;
      if (url) URL.revokeObjectURL(url);
    };
  }, [idx, photos, token]);

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape") onClose();
      else if (e.key === "ArrowRight") setIdx((i) => (i + 1) % photos.length);
      else if (e.key === "ArrowLeft") setIdx((i) => (i - 1 + photos.length) % photos.length);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [photos.length, onClose]);

  if (!photos.length) return null;
  const next = () => setIdx((i) => (i + 1) % photos.length);
  const prev = () => setIdx((i) => (i - 1 + photos.length) % photos.length);

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center"
      style={{ background: "rgba(0,0,0,0.95)" }}
      data-testid="photo-gallery"
      onContextMenu={(e) => e.preventDefault()}
    >
      <button
        type="button"
        onClick={onClose}
        className="absolute top-4 right-4 p-2 rounded-full bg-white/5 hover:bg-white/10 transition-colors z-10"
        data-testid="gallery-close"
      >
        <X className="w-5 h-5 text-white" />
      </button>

      {photos.length > 1 && (
        <>
          <button
            type="button"
            onClick={(e) => { e.stopPropagation(); prev(); }}
            aria-label="Previous photo"
            className="absolute left-3 sm:left-6 top-1/2 -translate-y-1/2 p-4 rounded-full transition-colors z-20 shadow-lg"
            style={{
              background: "rgba(197,160,89,0.92)",
              color: "#0A0A0C",
              border: "1px solid rgba(197,160,89,1)",
            }}
            data-testid="gallery-prev"
          >
            <ChevronLeft className="w-7 h-7" />
          </button>
          <button
            type="button"
            onClick={(e) => { e.stopPropagation(); next(); }}
            aria-label="Next photo"
            className="absolute right-3 sm:right-6 top-1/2 -translate-y-1/2 p-4 rounded-full transition-colors z-20 shadow-lg"
            style={{
              background: "rgba(197,160,89,0.92)",
              color: "#0A0A0C",
              border: "1px solid rgba(197,160,89,1)",
            }}
            data-testid="gallery-next"
          >
            <ChevronRight className="w-7 h-7" />
          </button>
        </>
      )}

      <div className="w-full h-full flex flex-col items-center justify-center p-8 pb-24">
        {src ? (
          <img
            src={src}
            alt={photos[idx].original_filename}
            onClick={(e) => { e.stopPropagation(); setZoomed((z) => !z); }}
            className={`max-w-full max-h-[70vh] object-contain select-none transition-transform ${zoomed ? "cursor-zoom-out scale-150" : "cursor-zoom-in"}`}
            draggable={false}
            data-testid="gallery-image"
          />
        ) : (
          <span className="spin" style={{ borderTopColor: "var(--vault-gold)" }} />
        )}
        <p className="mt-4 overline text-[var(--vault-text-muted)] font-mono-vault">
          {idx + 1} / {photos.length}
        </p>
      </div>

      {photos.length > 1 && (
        <div
          className="absolute bottom-3 left-1/2 -translate-x-1/2 flex gap-1.5 z-20"
          data-testid="gallery-dots"
        >
          {photos.map((_, i) => (
            <button
              key={i}
              type="button"
              aria-label={`Go to photo ${i + 1}`}
              onClick={(e) => { e.stopPropagation(); setIdx(i); }}
              className="w-2.5 h-2.5 rounded-full transition-colors"
              style={{
                background: i === idx ? "var(--vault-gold)" : "rgba(255,255,255,0.35)",
              }}
            />
          ))}
        </div>
      )}
    </div>
  );
}
